import React, { useState } from 'react';
import axios, { type AxiosError } from 'axios';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { RelativeTime } from '@/components/RelativeTime';
import {
  AlertCircle,
  Check,
  Copy,
  Eye,
  Lock,
  MoreHorizontal,
  Pencil,
  Plus,
  ShieldCheck,
  Trash2,
  X,
} from 'lucide-react';
import { config } from '../../config';
import { useAuth } from '../../hooks/useAuth';
import { type Role, useRoles } from '../../hooks/useRoles';
import { type Permission, usePermissions } from '../../hooks/usePermissions';
import ConfirmationModal from '../../components/ConfirmationModal';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AppSheet } from '@/components/ui/app-sheet';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PageContainer } from '@/components/ui/page-container';
import { PageHeader } from '@/components/ui/page-header';
import {
  ResponsiveTable,
  type ResponsiveTableColumn,
} from '@/components/ui/responsive-table';
import { EmptyState } from '@/components/ui/empty-state';
import { Textarea } from '@/components/ui/textarea';

interface CreateRoleRequest {
  name: string;
  description?: string;
  permissionIds?: string[];
}

interface UpdateRoleRequest {
  name?: string;
  description?: string;
}

interface RolePermission {
  id: string;
  name: string;
  appliesTo: string;
}

interface ApiErrorResponse {
  error: string;
  description?: string;
}

export default function Roles() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const organizationId = user?.organizationId;

  const [isCreateModalActive, setIsCreateModalActive] = useState(false);
  const [isEditModalActive, setIsEditModalActive] = useState(false);
  const [isPermissionsModalActive, setIsPermissionsModalActive] = useState(false);
  const [isViewPermissionsModalActive, setIsViewPermissionsModalActive] = useState(false);
  const [isDeleteConfirmActive, setIsDeleteConfirmActive] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [viewingRole, setViewingRole] = useState<Role | null>(null);
  const [roleToDelete, setRoleToDelete] = useState<Role | null>(null);
  const [cloneFromRoleId, setCloneFromRoleId] = useState('');

  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [selectedPermissions, setSelectedPermissions] = useState<Set<string>>(new Set());

  const [editFormName, setEditFormName] = useState('');
  const [editFormDescription, setEditFormDescription] = useState('');

  const [submitError, setSubmitError] = useState<string | null>(null);

  const backendApi = config.backendApi;

  const { data: roles, isLoading, isError, error } = useRoles(organizationId);
  const { data: permissions } = usePermissions();

  const { data: rolePermissions } = useQuery<RolePermission[]>({
    queryKey: ['role-permissions', selectedRole?.id, backendApi, selectedRole],
    queryFn: async () => {
      if (!selectedRole) return [];
      const response = await axios.get<{ permissions: RolePermission[] }>(
        `${backendApi}/api/roles/${selectedRole.id}/permissions`,
        { withCredentials: true },
      );
      return response.data.permissions;
    },
    enabled: !!selectedRole,
  });

  const { data: viewRolePermissions } = useQuery<RolePermission[]>({
    queryKey: ['role-permissions', viewingRole?.id, backendApi, viewingRole],
    queryFn: async () => {
      if (!viewingRole) return [];
      const response = await axios.get<{ permissions: RolePermission[] }>(
        `${backendApi}/api/roles/${viewingRole.id}/permissions`,
        { withCredentials: true },
      );
      return response.data.permissions;
    },
    enabled: !!viewingRole,
  });

  const createRoleMutation = useMutation<
    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
    void,
    AxiosError<ApiErrorResponse>,
    CreateRoleRequest
  >({
    mutationFn: async (newRole) => {
      await axios.post(`${backendApi}/api/roles`, newRole, { withCredentials: true });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['roles'] });
      setIsCreateModalActive(false);
      resetCreateForm();
      setSubmitError(null);
    },
    onError: (err) => {
      setSubmitError(
        err.response?.data.description || err.response?.data.error || 'Failed to create role.',
      );
    },
  });

  const updateRoleMutation = useMutation<
    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
    void,
    AxiosError<ApiErrorResponse>,
    { id: string; data: UpdateRoleRequest }
  >({
    mutationFn: async ({ id, data }) => {
      await axios.put(`${backendApi}/api/roles/${id}`, data, { withCredentials: true });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['roles'] });
      setIsEditModalActive(false);
      setEditingRole(null);
      setEditFormName('');
      setEditFormDescription('');
      setSubmitError(null);
    },
    onError: (err) => {
      setSubmitError(
        err.response?.data.description || err.response?.data.error || 'Failed to update role.',
      );
    },
  });

  const deleteRoleMutation = useMutation<
    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
    void,
    AxiosError<ApiErrorResponse>,
    string
  >({
    mutationFn: async (id) => {
      await axios.delete(`${backendApi}/api/roles/${id}`, { withCredentials: true });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['roles'] });
      setSubmitError(null);
    },
    onError: (err) => {
      setSubmitError(
        err.response?.data.description || err.response?.data.error || 'Failed to delete role.',
      );
    },
  });

  const updateRolePermissionsMutation = useMutation<
    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
    void,
    AxiosError<ApiErrorResponse>,
    { roleId: string; permissionIds: string[] }
  >({
    mutationFn: async ({ roleId, permissionIds }) => {
      await axios.put(
        `${backendApi}/api/roles/${roleId}/permissions`,
        { permissionIds },
        { withCredentials: true },
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['role-permissions'] });
      setIsPermissionsModalActive(false);
      setSelectedRole(null);
      setSubmitError(null);
    },
    onError: (err) => {
      setSubmitError(
        err.response?.data.description ||
          err.response?.data.error ||
          'Failed to update role permissions.',
      );
    },
  });

  const resetCreateForm = () => {
    setFormName('');
    setFormDescription('');
    setSelectedPermissions(new Set());
    setCloneFromRoleId('');
  };

  const handleCreateRole = (e: React.SubmitEvent) => {
    e.preventDefault();
    createRoleMutation.mutate({
      name: formName,
      description: formDescription || undefined,
      permissionIds: Array.from(selectedPermissions),
    });
  };

  const handleUpdateRole = (e: React.SubmitEvent) => {
    e.preventDefault();
    if (editingRole) {
      updateRoleMutation.mutate({
        id: editingRole.id,
        data: { name: editFormName, description: editFormDescription || undefined },
      });
    }
  };

  const handleUpdatePermissions = (e: React.SubmitEvent) => {
    e.preventDefault();
    if (selectedRole) {
      updateRolePermissionsMutation.mutate({
        roleId: selectedRole.id,
        permissionIds: Array.from(selectedPermissions),
      });
    }
  };

  const handleDeleteRole = (role: Role) => {
    if (!role.isMutable) {
      setSubmitError('Cannot delete system default roles. They are read-only.');
      return;
    }
    setRoleToDelete(role);
    setIsDeleteConfirmActive(true);
  };

  const confirmDeleteRole = () => {
    if (roleToDelete) {
      deleteRoleMutation.mutate(roleToDelete.id);
      setIsDeleteConfirmActive(false);
      setRoleToDelete(null);
    }
  };

  const cancelDeleteRole = () => {
    setIsDeleteConfirmActive(false);
    setRoleToDelete(null);
  };

  const openCreateModal = () => {
    resetCreateForm();
    setSubmitError(null);
    setIsCreateModalActive(true);
  };

  const openEditModal = (role: Role) => {
    if (!role.isMutable) {
      setSubmitError('Cannot edit system default roles. They are read-only.');
      return;
    }
    setEditingRole(role);
    setEditFormName(role.name);
    setEditFormDescription(role.description || '');
    setSubmitError(null);
    setIsEditModalActive(true);
  };

  const openPermissionsModal = (role: Role) => {
    setSelectedRole(role);
    setIsPermissionsModalActive(true);
  };

  const openViewPermissionsModal = (role: Role) => {
    setViewingRole(role);
    setIsViewPermissionsModalActive(true);
  };

  const openCloneModal = async (role: Role) => {
    try {
      const response = await axios.get<{ permissions: RolePermission[] }>(
        `${backendApi}/api/roles/${role.id}/permissions`,
        { withCredentials: true },
      );
      const permissionIds = response.data.permissions.map((p) => p.id);
      setFormName(`${role.name} (Copy)`);
      setFormDescription(role.description || '');
      setSelectedPermissions(new Set(permissionIds));
      setCloneFromRoleId(role.id);
      setSubmitError(null);
      setIsCreateModalActive(true);
    } catch {
      setSubmitError('Failed to fetch role permissions for cloning.');
    }
  };

  const togglePermission = (permissionId: string) => {
    const newSelected = new Set(selectedPermissions);
    if (newSelected.has(permissionId)) newSelected.delete(permissionId);
    else newSelected.add(permissionId);
    setSelectedPermissions(newSelected);
  };

  // Seed the checkboxes each time the modal opens (and when its permissions load).
  // Tracking `undefined` while closed is what makes a reopen re-seed even when the
  // cached permissions are the same object.
  const permissionsToSeed = isPermissionsModalActive ? rolePermissions : undefined;
  const [seededPermissions, setSeededPermissions] = useState<typeof rolePermissions>(undefined);
  if (permissionsToSeed !== seededPermissions) {
    setSeededPermissions(permissionsToSeed);
    if (permissionsToSeed) setSelectedPermissions(new Set(permissionsToSeed.map((p) => p.id)));
  }

  if (isLoading) {
    return (
      <PageContainer>
        <PageHeader title="Roles" />
        <p className="text-sm text-muted-foreground">Loading roles…</p>
      </PageContainer>
    );
  }

  if (isError) {
    return (
      <PageContainer>
        <PageHeader title="Roles" />
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>Error loading roles: {error.message}</AlertDescription>
        </Alert>
      </PageContainer>
    );
  }

  const groupedPermissions =
    permissions?.reduce<Partial<Record<string, Permission[]>>>((acc, permission) => {
      if (!acc[permission.appliesTo]) acc[permission.appliesTo] = [];
      acc[permission.appliesTo]?.push(permission);
      return acc;
    }, {}) || {};

  const renderPermissionsList = (onToggle: (id: string) => void) => (
    <div className="rounded-md border border-border-subtle bg-muted/30 p-3 max-h-[400px] overflow-y-auto">
      {Object.entries(groupedPermissions)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([category, perms]) => {
          if (!perms) return null;
          return (
            <div key={category} className="mb-4">
              <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-2">
                {category}
              </p>
              <div className="space-y-1.5">
                {perms.map((permission) => (
                  <label
                    key={permission.id}
                    className="flex items-start gap-2 text-sm cursor-pointer"
                  >
                    <Checkbox
                      checked={selectedPermissions.has(permission.id)}
                      onCheckedChange={() => { onToggle(permission.id); }}
                      className="mt-0.5"
                    />
                    <div>
                      <code className="font-mono text-xs">{permission.name}</code>
                      {permission.description ? (
                        <span className="text-xs text-muted-foreground ml-2">
                          — {permission.description}
                        </span>
                      ) : null}
                    </div>
                  </label>
                ))}
              </div>
            </div>
          );
        })}
    </div>
  );

  const roleColumns: readonly ResponsiveTableColumn<Role>[] = [
    {
      key: 'name',
      label: 'Name',
      hideMobileLabel: true,
      render: (role) => <span className="font-medium">{role.name}</span>,
    },
    {
      key: 'description',
      label: 'Description',
      render: (role) => (
        <span className="text-xs text-muted-foreground">
          {role.description || 'No description'}
        </span>
      ),
    },
    {
      key: 'type',
      label: 'Type',
      render: (role) => (
        <Badge
          className={
            role.isGlobal
              ? 'bg-sev-3/15 text-foreground border-0 font-normal gap-1'
              : 'bg-sev-4/15 text-sev-4 border-0 font-normal'
          }
        >
          {role.isGlobal ? <Lock className="w-3 h-3" /> : null}
          {role.isGlobal ? 'System default' : 'Custom role'}
        </Badge>
      ),
    },
    {
      key: 'created',
      label: 'Created',
      hideAt: 'mobile',
      render: (role) =>
        role.createdAt ? (
          <span className="text-xs text-muted-foreground">
            <RelativeTime value={role.createdAt} />
          </span>
        ) : null,
    },
  ];

  return (
    <PageContainer>
      <PageHeader
        title="Roles"
        subtitle="Manage roles and their permissions"
        primary={
          <Button onClick={openCreateModal} className="gap-1.5">
            <Plus className="w-4 h-4" />
            Create custom role
          </Button>
        }
      />

      {submitError ? (
        <Alert variant="destructive" className="mb-4 relative">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{submitError}</AlertDescription>
          <button
            type="button"
            onClick={() => { setSubmitError(null); }}
            className="absolute top-2 right-2 inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-accent"
            aria-label="Dismiss"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </Alert>
      ) : null}

      <ResponsiveTable<Role>
        columns={roleColumns}
        rows={roles ?? []}
        rowKey={(r) => r.id}
        isLoading={isLoading}
        rowActions={(role) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label={`Actions for ${role.name}`}
              >
                <MoreHorizontal className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={() => { openViewPermissionsModal(role); }}
                className="gap-2"
              >
                <Eye className="w-4 h-4" />
                View permissions
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => { void openCloneModal(role); }}
                className="gap-2"
              >
                <Copy className="w-4 h-4" />
                Clone
              </DropdownMenuItem>
              {role.isMutable ? (
                <>
                  <DropdownMenuItem
                    onClick={() => { openPermissionsModal(role); }}
                    className="gap-2"
                  >
                    <ShieldCheck className="w-4 h-4" />
                    Manage permissions
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => { openEditModal(role); }}
                    className="gap-2"
                  >
                    <Pencil className="w-4 h-4" />
                    Edit
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => { handleDeleteRole(role); }}
                    className="gap-2 text-sev-1 focus:text-sev-1"
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete
                  </DropdownMenuItem>
                </>
              ) : null}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        empty={<EmptyState variant="no-data" title="No roles found" />}
      />

      <AppSheet
        open={isCreateModalActive}
        onOpenChange={(open) => { if (!open) setIsCreateModalActive(false); }}
        title={cloneFromRoleId ? 'Clone role' : 'Create custom role'}
        size="lg"
      >
        <AppSheet.Body>
          <form id="create-role-form" onSubmit={handleCreateRole} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="cr-name">Name</Label>
              <Input
                id="cr-name"
                type="text"
                placeholder="Role name"
                value={formName}
                onChange={(e) => { setFormName(e.target.value); }}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cr-desc">Description (optional)</Label>
              <Textarea
                id="cr-desc"
                placeholder="Role description or purpose"
                value={formDescription}
                onChange={(e) => { setFormDescription(e.target.value); }}
                rows={2}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Permissions</Label>
              {renderPermissionsList(togglePermission)}
            </div>
            {submitError ? (
              <Alert variant="destructive">
                <AlertDescription>{submitError}</AlertDescription>
              </Alert>
            ) : null}
          </form>
        </AppSheet.Body>
        <AppSheet.Footer>
          <Button
            variant="outline"
            type="button"
            onClick={() => { setIsCreateModalActive(false); }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="create-role-form"
            disabled={createRoleMutation.isPending}
          >
            {createRoleMutation.isPending
              ? 'Saving…'
              : cloneFromRoleId
                ? 'Clone role'
                : 'Create role'}
          </Button>
        </AppSheet.Footer>
      </AppSheet>

      <AppSheet
        open={isEditModalActive}
        onOpenChange={(open) => { if (!open) setIsEditModalActive(false); }}
        title={`Edit role: ${editingRole?.name ?? ''}`}
        size="md"
      >
        <AppSheet.Body>
          <form id="edit-role-form" onSubmit={handleUpdateRole} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="er-name">Name</Label>
              <Input
                id="er-name"
                type="text"
                placeholder="Role name"
                value={editFormName}
                onChange={(e) => { setEditFormName(e.target.value); }}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="er-desc">Description (optional)</Label>
              <Textarea
                id="er-desc"
                placeholder="Role description or purpose"
                value={editFormDescription}
                onChange={(e) => { setEditFormDescription(e.target.value); }}
                rows={3}
              />
            </div>
            {submitError ? (
              <Alert variant="destructive">
                <AlertDescription>{submitError}</AlertDescription>
              </Alert>
            ) : null}
          </form>
        </AppSheet.Body>
        <AppSheet.Footer>
          <Button
            variant="outline"
            type="button"
            onClick={() => { setIsEditModalActive(false); }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="edit-role-form"
            disabled={updateRoleMutation.isPending}
          >
            {updateRoleMutation.isPending ? 'Saving…' : 'Update role'}
          </Button>
        </AppSheet.Footer>
      </AppSheet>

      <AppSheet
        open={isPermissionsModalActive}
        onOpenChange={(open) => { if (!open) setIsPermissionsModalActive(false); }}
        title={`Manage permissions: ${selectedRole?.name ?? ''}`}
        size="lg"
      >
        <AppSheet.Body>
          <form id="manage-perms-form" onSubmit={handleUpdatePermissions}>
            {renderPermissionsList(togglePermission)}
            {submitError ? (
              <Alert variant="destructive" className="mt-4">
                <AlertDescription>{submitError}</AlertDescription>
              </Alert>
            ) : null}
          </form>
        </AppSheet.Body>
        <AppSheet.Footer>
          <Button
            variant="outline"
            type="button"
            onClick={() => { setIsPermissionsModalActive(false); }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="manage-perms-form"
            disabled={updateRolePermissionsMutation.isPending}
          >
            {updateRolePermissionsMutation.isPending ? 'Saving…' : 'Update permissions'}
          </Button>
        </AppSheet.Footer>
      </AppSheet>

      <Dialog
        open={isViewPermissionsModalActive}
        onOpenChange={(open) => { if (!open) setIsViewPermissionsModalActive(false); }}
      >
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>View permissions: {viewingRole?.name}</DialogTitle>
          </DialogHeader>
          {viewingRole?.isGlobal ? (
            <Alert className="border-sev-5/30 bg-sev-5/5">
              <Lock className="h-4 w-4" />
              <AlertDescription>
                <strong>System default role.</strong> This is a system-defined role and
                cannot be modified. You can clone this role to create a custom version.
              </AlertDescription>
            </Alert>
          ) : null}
          <div className="rounded-md border border-border-subtle bg-muted/30 p-3 max-h-[500px] overflow-y-auto">
            {viewRolePermissions && viewRolePermissions.length > 0 ? (
              Object.entries(
                viewRolePermissions.reduce<Partial<Record<string, RolePermission[]>>>(
                  (acc, permission) => {
                    if (!acc[permission.appliesTo]) acc[permission.appliesTo] = [];
                    acc[permission.appliesTo]?.push(permission);
                    return acc;
                  },
                  {},
                ),
              )
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([category, perms]) => {
                  if (!perms) return null;
                  return (
                    <div key={category} className="mb-4">
                      <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-2">
                        {category}
                      </p>
                      <div className="space-y-1">
                        {perms.map((permission) => (
                          <div
                            key={permission.id}
                            className="flex items-center gap-2 text-sm"
                          >
                            <Check className="w-4 h-4 text-sev-4 shrink-0" />
                            <code className="font-mono text-xs">{permission.name}</code>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })
            ) : (
              <p className="text-sm text-muted-foreground">
                No permissions assigned to this role.
              </p>
            )}
          </div>
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => { setIsViewPermissionsModalActive(false); }}
            >
              Close
            </Button>
            <Button
              variant="default"
              onClick={() => {
                setIsViewPermissionsModalActive(false);
                if (viewingRole) void openCloneModal(viewingRole);
              }}
              className="gap-1.5"
            >
              <Copy className="w-4 h-4" />
              Clone this role
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmationModal
        isActive={isDeleteConfirmActive}
        title="Delete role"
        message={`Are you sure you want to delete the role "${roleToDelete?.name || 'Unknown'}"? This action cannot be undone and will affect all teams and users with this role.`}
        confirmText="Delete role"
        cancelText="Cancel"
        onConfirm={confirmDeleteRole}
        onCancel={cancelDeleteRole}
        isLoading={deleteRoleMutation.isPending}
        isDanger={true}
      />
    </PageContainer>
  );
}
